import React from "react";
import { useMemo, useContext, useState, useCallback } from "react";
import clsx from "clsx";

import { Context } from "../../../teacherStore";
import {
  LessonContents,
  LESSON_CONTENTS,
} from "../../../constants/active_learning";
import ToggleMaterialAndAnswerButton from "../../../molecules/ActiveLearning/ToggleMaterialAndAnswerButton";
import EndLesson from "../../../organisms/ActiveLearning/EndLesson";
import Header from "../../../organisms/ActiveLearning/Header";

import Lesson1Material from "./Lesson1/Material";
import Lesson2Material from "./Lesson2/Material";
import Lesson3Material from "./Lesson3/Material";
import Lesson4Material from "./Lesson4/Material";
import Lesson5Material from "./Lesson5/Material";
import Lesson6Material from "./Lesson6/Material";
import Lesson7Material from "./Lesson7/Material";
import Lesson8Material from "./Lesson8/Material";
import Lesson9Material from "./Lesson9/Material";
import Lesson10Material from "./Lesson10/Material";
import Lesson11Material from "./Lesson11/Material";
import Lesson12Material from "./Lesson12/Material";
import Lesson13Material from "./Lesson13/Material";
import Lesson14Material from "./Lesson14/Material";
import Lesson15Material from "./Lesson15/Material";
import Lesson1Answer from "./Lesson1/Answer";
import Lesson2Answer from "./Lesson2/Answer";
import Lesson3Answer from "./Lesson3/Answer";
import Lesson4Answer from "./Lesson4/Answer";
import Lesson5Answer from "./Lesson5/Answer";
import Lesson6Answer from "./Lesson6/Answer";
import Lesson7Answer from "./Lesson7/Answer";
import Lesson8Answer from "./Lesson8/Answer";
import Lesson9Answer from "./Lesson9/Answer";
import Lesson10Answer from "./Lesson10/Answer";
import Lesson11Answer from "./Lesson11/Answer";
import Lesson12Answer from "./Lesson12/Answer";
import Lesson13Answer from "./Lesson13/Answer";
import Lesson14Answer from "./Lesson14/Answer";
import Lesson15Answer from "./Lesson15/Answer";
import styles from "./ActiveLearningLesson.module.scss";

type ActiveLearningLessonsProps = {
  materialCode: string;
  lessonNo: number;
};

export const ActiveLearningLessons: React.FC<ActiveLearningLessonsProps> = ({
  materialCode,
  lessonNo,
}) => {
  const { state } = useContext(Context);
  const [viewContentType, setViewContentType] = useState<"material" | "answer">(
    "material",
  );

  const currentActiveLearning = useMemo(() => {
    return state.active_learning_material_lessons;
  }, [state.active_learning_material_lessons]);

  const findIssue = useCallback(
    (no: number) =>
      currentActiveLearning.issues?.find(issue => issue.no === no),
    [currentActiveLearning],
  );

  const LessonMaterialComponents = {
    lesson1: <Lesson1Material findIssue={findIssue} />,
    lesson2: <Lesson2Material findIssue={findIssue} />,
    lesson3: <Lesson3Material findIssue={findIssue} />,
    lesson4: <Lesson4Material findIssue={findIssue} />,
    lesson5: <Lesson5Material findIssue={findIssue} />,
    lesson6: <Lesson6Material findIssue={findIssue} />,
    lesson7: <Lesson7Material findIssue={findIssue} />,
    lesson8: <Lesson8Material findIssue={findIssue} />,
    lesson9: <Lesson9Material findIssue={findIssue} />,
    lesson10: <Lesson10Material findIssue={findIssue} />,
    lesson11: <Lesson11Material findIssue={findIssue} />,
    lesson12: <Lesson12Material findIssue={findIssue} />,
    lesson13: <Lesson13Material findIssue={findIssue} />,
    lesson14: <Lesson14Material findIssue={findIssue} />,
    lesson15: <Lesson15Material findIssue={findIssue} />,
  };
  const LessonAnswerComponents = {
    lesson1: (
      <Lesson1Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson2: (
      <Lesson2Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson3: (
      <Lesson3Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson4: (
      <Lesson4Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson5: (
      <Lesson5Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson6: (
      <Lesson6Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson7: (
      <Lesson7Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson8: (
      <Lesson8Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson9: (
      <Lesson9Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson10: (
      <Lesson10Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson11: (
      <Lesson11Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson12: (
      <Lesson12Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson13: (
      <Lesson13Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson14: (
      <Lesson14Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
    lesson15: (
      <Lesson15Answer
        materialCode={materialCode}
        lessonNo={lessonNo}
        findIssue={findIssue}
      />
    ),
  };

  const LessonComponents =
    viewContentType === "material"
      ? LessonMaterialComponents
      : LessonAnswerComponents;

  const howRoundsOfLesson = useCallback((): number => {
    switch (materialCode) {
      case "ECS001":
        return 1;
      case "ECS002":
        return 2;
      default:
        return NaN;
    }
  }, [materialCode]);

  const getLessonContentsByNoRound1 = (): LessonContents => {
    return (() => {
      switch (lessonNo) {
        case 1:
          return LESSON_CONTENTS.lesson1;
        case 2:
          return LESSON_CONTENTS.lesson2;
        case 3:
          return LESSON_CONTENTS.lesson3;
        case 4:
          return LESSON_CONTENTS.lesson4;
        case 5:
          return LESSON_CONTENTS.lesson5;
        case 6:
          return LESSON_CONTENTS.lesson6;
        case 7:
          return LESSON_CONTENTS.lesson7;
        case 8:
          return LESSON_CONTENTS.lesson8;
        case 9:
          return LESSON_CONTENTS.lesson9;
        case 10:
          return LESSON_CONTENTS.lesson10;
        case 11:
          return LESSON_CONTENTS.lesson11;
        default:
          return { label: "", read: "", end: "" };
      }
    })();
  };

  const getLessonContentsByNoRound2 = (): LessonContents => {
    return (() => {
      switch (lessonNo) {
        case 1:
          return LESSON_CONTENTS.lesson1;
        case 2:
          return LESSON_CONTENTS.lesson12;
        case 3:
          return LESSON_CONTENTS.lesson13;
        case 4:
          return LESSON_CONTENTS.lesson14;
        case 5:
          return LESSON_CONTENTS.lesson4;
        case 6:
          return LESSON_CONTENTS.lesson5;
        case 7:
          return LESSON_CONTENTS.lesson6;
        case 8:
          return LESSON_CONTENTS.lesson7;
        case 9:
          return LESSON_CONTENTS.lesson8;
        case 10:
          return LESSON_CONTENTS.lesson9;
        case 11:
          return LESSON_CONTENTS.lesson10;
        case 12:
          return LESSON_CONTENTS.lesson15;
        default:
          return { label: "", read: "", end: "" };
      }
    })();
  };

  const LessonContents =
    howRoundsOfLesson() === 1
      ? getLessonContentsByNoRound1()
      : getLessonContentsByNoRound2();

  const getLessonComponentsByNoRound1 = () => {
    return (() => {
      switch (lessonNo) {
        case 1:
          return LessonComponents.lesson1;
        case 2:
          return LessonComponents.lesson2;
        case 3:
          return LessonComponents.lesson3;
        case 4:
          return LessonComponents.lesson4;
        case 5:
          return LessonComponents.lesson5;
        case 6:
          return LessonComponents.lesson6;
        case 7:
          return LessonComponents.lesson7;
        case 8:
          return LessonComponents.lesson8;
        case 9:
          return LessonComponents.lesson9;
        case 10:
          return LessonComponents.lesson10;
        case 11:
          return LessonComponents.lesson11;
        default:
          return "Not Found";
      }
    })();
  };

  const getLessonComponentsByNoRound2 = () => {
    return (() => {
      switch (lessonNo) {
        case 1:
          return LessonComponents.lesson1;
        case 2:
          return LessonComponents.lesson12;
        case 3:
          return LessonComponents.lesson13;
        case 4:
          return LessonComponents.lesson14;
        case 5:
          return LessonComponents.lesson4;
        case 6:
          return LessonComponents.lesson5;
        case 7:
          return LessonComponents.lesson6;
        case 8:
          return LessonComponents.lesson7;
        case 9:
          return LessonComponents.lesson8;
        case 10:
          return LessonComponents.lesson9;
        case 11:
          return LessonComponents.lesson10;
        case 12:
          return LessonComponents.lesson15;
        default:
          return "Not Found";
      }
    })();
  };

  return (
    <div className={styles.wrapper} id="accordion">
      {/* <p>answer: {JSON.stringify(answers)}</p> */}
      <Header
        no={lessonNo}
        label={LessonContents.label}
        title={currentActiveLearning.title}
        read={LessonContents.read}
        isTeacher
      />
      <ToggleMaterialAndAnswerButton
        contents={viewContentType}
        setViewContentType={setViewContentType}
      />
      <div
        className={clsx(
          styles.container,
          viewContentType === "material" && styles.isDisabled,
        )}
      >
        {howRoundsOfLesson() === 1
          ? getLessonComponentsByNoRound1()
          : getLessonComponentsByNoRound2()}
        <div className={clsx(styles.section, styles.isEnabled)}>
          <EndLesson text={LessonContents.end} isTeacher />
        </div>
      </div>
    </div>
  );
};
export default ActiveLearningLessons;
